import React from 'react';
import { Button, Burger } from '@mantine/core';
import { useDisclosure } from '@mantine/hooks';
import { Link } from 'react-router-dom';
import styled from '@emotion/styled';
import { Group } from '@mantine/core';
import logoSrc from '../../../assets/images/logo.svg';
import { MARKETING_ROUTES, PUBLIC_ROUTES } from '../../routing/routes';

/**
 * PublicHeader Component
 *
 * Sprint 041 Session 1: Marketing-style header for public/marketing pages.
 * All navigation uses internal React Router Links (same-domain after consolidation).
 * Adapted from Marketing site's Header.tsx and Frontend's MarketingHeader.
 *
 * Features:
 * - Logo linking to home
 * - Desktop navigation with marketing page links (internal routing)
 * - Login and Sign Up buttons
 * - Mobile responsive with burger menu
 * - Sticky positioning at top of viewport
 */
export const PublicHeader: React.FC = (): React.ReactElement => {
  const [opened, { toggle, close }] = useDisclosure(false);

  const handleNavClick = (): void => {
    close();
  };

  return (
    <HeaderWrapper>
      <HeaderContent>
        <InnerContainer>
          <LeftGroup>
            <LogoLink to={MARKETING_ROUTES.HOME} aria-label="MentionedMe - Go to home page">
              <Logo src={logoSrc} alt="MentionedMe" />
            </LogoLink>

            <DesktopNav>
              <NavLink to={MARKETING_ROUTES.BENEFITS}>Benefits</NavLink>
              <NavLink to={MARKETING_ROUTES.FEATURES}>Features</NavLink>
              <NavLink to={MARKETING_ROUTES.EXAMPLES}>Examples</NavLink>
              <NavLink to={MARKETING_ROUTES.HOW_IT_WORKS}>How It Works</NavLink>
              <NavLink to={MARKETING_ROUTES.SOCIAL_IMPACT}>For Non-Profits</NavLink>
              <NavLink to={MARKETING_ROUTES.CONTACT}>Contact</NavLink>
            </DesktopNav>
          </LeftGroup>

          <DesktopButtonGroup>
            <Button
              component={Link}
              to={PUBLIC_ROUTES.LOGIN}
              variant="subtle"
              color="violet"
            >
              Login
            </Button>
            <Button
              component={Link}
              to={PUBLIC_ROUTES.REGISTER}
              variant="filled"
              color="violet"
            >
              Sign Up
            </Button>
          </DesktopButtonGroup>

          <MobileBurger>
            <Burger opened={opened} onClick={toggle} aria-label="Toggle navigation" />
          </MobileBurger>
        </InnerContainer>

        {opened && (
          <MobileNav>
            <MobileNavLink to={MARKETING_ROUTES.BENEFITS} onClick={handleNavClick}>
              Benefits
            </MobileNavLink>
            <MobileNavLink to={MARKETING_ROUTES.FEATURES} onClick={handleNavClick}>
              Features
            </MobileNavLink>
            <MobileNavLink to={MARKETING_ROUTES.EXAMPLES} onClick={handleNavClick}>
              Examples
            </MobileNavLink>
            <MobileNavLink to={MARKETING_ROUTES.HOW_IT_WORKS} onClick={handleNavClick}>
              How It Works
            </MobileNavLink>
            <MobileNavLink to={MARKETING_ROUTES.SOCIAL_IMPACT} onClick={handleNavClick}>
              For Non-Profits
            </MobileNavLink>
            <MobileNavLink to={MARKETING_ROUTES.CONTACT} onClick={handleNavClick}>
              Contact
            </MobileNavLink>
            <MobileButtonGroup>
              <Button
                component={Link}
                to={PUBLIC_ROUTES.LOGIN}
                variant="subtle"
                color="violet"
                fullWidth
                onClick={handleNavClick}
              >
                Login
              </Button>
              <Button
                component={Link}
                to={PUBLIC_ROUTES.REGISTER}
                variant="filled"
                color="violet"
                fullWidth
                onClick={handleNavClick}
              >
                Sign Up
              </Button>
            </MobileButtonGroup>
          </MobileNav>
        )}
      </HeaderContent>
    </HeaderWrapper>
  );
};

const HeaderWrapper = styled.header`
  background-color: #ffffff;
  border-bottom: 1px solid #e9ecef;
  padding: 16px 0;
  position: sticky;
  top: 0;
  z-index: 100;
  box-shadow: 0 1px 3px rgba(0, 0, 0, 0.05);
`;

const HeaderContent = styled.div`
  width: 100%;
  max-width: 100%;
  padding: 0 24px;
  margin: 0 auto;

  @media (min-width: 768px) {
    padding: 0 48px;
  }

  @media (min-width: 1200px) {
    padding: 0 64px;
  }
`;

const InnerContainer = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 24px;
`;

const LeftGroup = styled.div`
  display: flex;
  align-items: center;
  gap: 48px;
`;

const LogoLink = styled(Link)`
  text-decoration: none;
  color: inherit;
  display: flex;
  align-items: center;
`;

const Logo = styled.img`
  height: 40px;
  width: auto;

  @media (min-width: 768px) {
    height: 50px;
  }
`;

const DesktopNav = styled.nav`
  display: none;
  gap: 24px;

  @media (min-width: 768px) {
    display: flex;
  }
`;

const NavLink = styled(Link)`
  color: #495057;
  font-weight: 500;
  font-size: 16px;
  text-decoration: none;
  transition: color 0.2s ease;

  &:hover {
    color: #4d18c4;
  }

  &:focus-visible {
    outline: 2px solid #4d18c4;
    outline-offset: 4px;
    border-radius: 4px;
  }
`;

const DesktopButtonGroup = styled(Group)`
  display: none;

  @media (min-width: 768px) {
    display: flex;
  }
`;

const MobileBurger = styled.div`
  display: block;

  @media (min-width: 768px) {
    display: none;
  }
`;

const MobileNav = styled.nav`
  display: flex;
  flex-direction: column;
  gap: 16px;
  padding: 24px 0;
  margin-top: 16px;
  border-top: 1px solid #e9ecef;

  @media (min-width: 768px) {
    display: none;
  }
`;

const MobileNavLink = styled(Link)`
  color: #495057;
  font-weight: 500;
  font-size: 18px;
  text-decoration: none;
  padding: 8px 0;
  transition: color 0.2s ease;

  &:hover {
    color: #4d18c4;
  }

  &:focus-visible {
    outline: 2px solid #4d18c4;
    outline-offset: 4px;
    border-radius: 4px;
  }
`;

const MobileButtonGroup = styled.div`
  display: flex;
  flex-direction: column;
  gap: 12px;
  margin-top: 16px;
`;
